import { Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import type { ContentTableRow } from './content-table-rows.ts';
import { Icon } from './icons.tsx';
import { compareContentTableRows, formatContentCreatedAt,
  readContentTableSort, sortContentTableRows, writeContentTableSort,
  type ContentTableSort, type ContentTableSortField } from './content-table-sort.ts';

interface ContentTableViewProps {
  rows: ContentTableRow[];
  accountId: string;
  focusedContentId: string | null;
  showPackage: boolean;
  showDeliveredOn: boolean;
  loading?: boolean;
  onOpen: (row: ContentTableRow) => void;
}

function TypeMark({ type }: { type: ContentTableRow['type'] }) {
  return <span className={`content-table-type ${type === 'ARTICLE' ? 'is-article' : ''}`}>{type === 'VIDEO' ? '视' : '文'}</span>;
}

function PublishCell({ publish }: { publish: ContentTableRow['publish'] }) {
  if (publish.kind !== 'platforms') return <span className="content-table-muted">{publish.text}</span>;
  return <div className="content-table-publish">{publish.platforms.map((platform) =>
    <span key={platform.label} className={`content-table-platform is-${platform.state.toLowerCase()}`}>
      {platform.label}{platform.state === 'SUCCEEDED' ? <b><Icon name="check" size={13} /></b> : platform.state === 'FAILED' ? <b><Icon name="close" size={13} /></b> : null}
    </span>)}</div>;
}

export function ContentTableView({ rows, accountId, focusedContentId, showPackage, showDeliveredOn,
  loading = false, onOpen }: ContentTableViewProps) {
  const [sort, setSort] = useState<ContentTableSort>(() => readContentTableSort(
    typeof window === 'undefined' ? null : window.localStorage, accountId));
  useEffect(() => setSort(readContentTableSort(
    typeof window === 'undefined' ? null : window.localStorage, accountId)), [accountId]);
  const sortedRows = useMemo(() => sortContentTableRows(rows, sort), [rows, sort]);
  const sortable = (field: ContentTableSortField) => ({
    sorter: (left: ContentTableRow, right: ContentTableRow) => compareContentTableRows(left, right, field),
    sortOrder: sort.field === field ? sort.order : null,
    sortDirections: ['descend', 'ascend', 'descend'] as Array<'descend' | 'ascend'>,
  });
  const columns: TableColumnsType<ContentTableRow> = [
    { title: '未结修改数', dataIndex: 'issues', key: 'issues', width: 76, align: 'center',
      render: (issues: number) => <span className={`content-table-issue ${issues ? 'has' : ''}`} title="未解决且未作废的问题数（含修改意见）">{issues}</span> },
    { title: '内容名称', dataIndex: 'title', key: 'title', width: 320,
      render: (_value, row) => <div className="content-table-name"><TypeMark type={row.type} />
        <button type="button" className="content-table-title" title={row.title}
          onClick={(event) => { event.stopPropagation(); onOpen(row); }}>{row.title}</button></div> },
    { title: '编号', dataIndex: 'no', key: 'no', width: 84, ...sortable('no'),
      render: (no: string) => <span className="content-table-no" title={no}>{no}</span> },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 142, ...sortable('createdAt'),
      render: (createdAt: string) => <span className="content-table-date">{formatContentCreatedAt(createdAt)}</span> },
    { title: '执行状态', dataIndex: 'statusLabel', key: 'status', width: 96,
      render: (label: string, row) => <span className={`content-table-pill is-${row.statusGroup.toLowerCase()}`}>{label}</span> },
    { title: '交付状态', dataIndex: 'deliveryLabel', key: 'delivery', width: 102,
      render: (label: string, row) => <span className={`content-table-pill is-${row.deliveryTone}`}>{label}</span> },
    { title: '发布情况', dataIndex: 'publish', key: 'publish', width: 186,
      render: (publish: ContentTableRow['publish']) => <PublishCell publish={publish} /> },
    { title: '难度级别', dataIndex: 'tierLabel', key: 'tier', width: 92,
      render: (label: string) => <span className="content-table-muted" title={label}>{label}</span> },
    { title: '策划运营', dataIndex: 'planner', key: 'planner', width: 96,
      render: (value: string) => <span className="content-table-person" title={value}>{value}</span> },
    { title: '剪辑人员', dataIndex: 'editor', key: 'editor', width: 96,
      render: (value: string) => <span className="content-table-person" title={value}>{value}</span> },
    ...(showDeliveredOn ? [{ title: '实际交付日期', dataIndex: 'deliveredOn', key: 'deliveredOn', width: 116,
      render: (value: string | null) => <span className="content-table-date">{value ?? '—'}</span> }] : []),
    ...(showPackage ? [{ title: '服务包', dataIndex: 'packageLabel', key: 'packageLabel', width: 176,
      render: (value: string) => <span className="content-table-package" title={value}>{value}</span> }] : []),
  ];

  return <section className="content-table-view" aria-label="内容表格视图">
    <Table<ContentTableRow>
      className="content-table"
      rowKey="contentId"
      size="small"
      columns={columns}
      dataSource={sortedRows}
      loading={loading}
      pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'],
        showTotal: (total) => `共 ${total} 条` }}
      scroll={{ x: showPackage ? 1522 : 1346 }}
      locale={{ emptyText: '没有符合当前筛选的内容' }}
      onChange={(_pagination, _filters, sorter) => {
        const active = Array.isArray(sorter) ? sorter[0] : sorter;
        if ((active?.columnKey === 'createdAt' || active?.columnKey === 'no') && active.order) {
          const next: ContentTableSort = { field: active.columnKey, order: active.order };
          setSort(next);
          writeContentTableSort(typeof window === 'undefined' ? null : window.localStorage, accountId, next);
        }
      }}
      rowClassName={(row) => row.contentId === focusedContentId ? 'is-focused' : ''}
      onRow={(row) => ({ tabIndex: 0, 'aria-label': `${row.no} ${row.title}`,
        onClick: () => onOpen(row),
        onKeyDown: (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(row); } } })}
    /></section>;
}
